import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, ImageIcon } from 'lucide-react';
import { ColorPicker } from './ColorPicker';
import { FontSelector } from './FontSelector';

export const SectionEditModal = ({ section, onClose, onUpdate, onOpenImagePicker }) => {
  if (!section) return null;

  const updateField = (field, value) => {
    onUpdate(section.id, field, value);
  };

  const updateArrayItem = (field, index, itemField, value) => {
    const items = [...(section.content[field] || [])];
    items[index] = { ...items[index], [itemField]: value };
    updateField(field, items);
  };

  const addArrayItem = (field, defaultItem) => {
    const items = [...(section.content[field] || []), defaultItem];
    updateField(field, items);
  };

  const removeArrayItem = (field, index) => {
    const items = section.content[field].filter((_, i) => i !== index);
    updateField(field, items);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Edit {section.type.charAt(0).toUpperCase() + section.type.slice(1)} Section</h2>
          <Button onClick={onClose} variant="ghost" size="sm">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* HERO SECTION */}
          {section.type === 'hero' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading</label>
                <Input
                  value={section.content.subheading}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Button Text</label>
                <Input
                  value={section.content.buttonText}
                  onChange={(e) => updateField('buttonText', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Button Link</label>
                <Input
                  value={section.content.buttonLink}
                  onChange={(e) => updateField('buttonLink', e.target.value)}
                  placeholder="#contact"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Background Image</label>
                <div className="space-y-2">
                  <Input
                    value={section.content.backgroundImage}
                    onChange={(e) => updateField('backgroundImage', e.target.value)}
                    placeholder="Enter image URL"
                  />
                  <Button
                    onClick={() => onOpenImagePicker(section.id, 'backgroundImage')}
                    variant="outline"
                    className="w-full"
                    type="button"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Browse/Upload Images
                  </Button>
                </div>
              </div>
              <ColorPicker
                label="Text Color"
                value={section.content.textColor || '#ffffff'}
                onChange={(color) => updateField('textColor', color)}
              />
            </>
          )}

          {/* ABOUT SECTION */}
          {section.type === 'about' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Text Content</label>
                <textarea
                  value={section.content.text}
                  onChange={(e) => updateField('text', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image</label>
                <div className="space-y-2">
                  <Input
                    value={section.content.image || ''}
                    onChange={(e) => updateField('image', e.target.value)}
                    placeholder="Enter image URL"
                  />
                  <Button
                    onClick={() => onOpenImagePicker(section.id, 'image')}
                    variant="outline"
                    className="w-full"
                    type="button"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Browse/Upload Images
                  </Button>
                </div>
              </div>
              <ColorPicker
                label="Background Color"
                value={section.content.backgroundColor || '#ffffff'}
                onChange={(color) => updateField('backgroundColor', color)}
              />
              <ColorPicker
                label="Text Color"
                value={section.content.textColor || '#1f2937'}
                onChange={(color) => updateField('textColor', color)}
              />
            </>
          )}

          {/* SERVICES SECTION */}
          {section.type === 'services' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Service Items</label>
                {section.content.items?.map((item, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Service {index + 1}</span>
                      <Button
                        onClick={() => removeArrayItem('items', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Icon (emoji)"
                        value={item.icon}
                        onChange={(e) => updateArrayItem('items', index, 'icon', e.target.value)}
                      />
                      <Input
                        placeholder="Title"
                        value={item.title}
                        onChange={(e) => updateArrayItem('items', index, 'title', e.target.value)}
                      />
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateArrayItem('items', index, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('items', { icon: '✨', title: 'New Service', description: 'Description here' })}
                  variant="outline"
                  className="w-full"
                >
                  + Add Service
                </Button>
              </div>
            </>
          )}

          {/* FEATURES SECTION */}
          {section.type === 'features' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading (optional)</label>
                <Input
                  value={section.content.subheading || ''}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Features</label>
                {section.content.features?.map((feature, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Feature {index + 1}</span>
                      <Button
                        onClick={() => removeArrayItem('features', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Icon (emoji)"
                        value={feature.icon}
                        onChange={(e) => updateArrayItem('features', index, 'icon', e.target.value)}
                      />
                      <Input
                        placeholder="Title"
                        value={feature.title}
                        onChange={(e) => updateArrayItem('features', index, 'title', e.target.value)}
                      />
                      <Input
                        placeholder="Description"
                        value={feature.description}
                        onChange={(e) => updateArrayItem('features', index, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('features', { icon: '✨', title: 'New Feature', description: 'Description here' })}
                  variant="outline"
                  className="w-full"
                >
                  + Add Feature
                </Button>
              </div>
            </>
          )}

          {/* GALLERY SECTION */}
          {section.type === 'gallery' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Gallery Images</label>
                {section.content.images?.map((img, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Image {index + 1}</span>
                      <Button
                        onClick={() => removeArrayItem('images', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Image URL"
                        value={img.url}
                        onChange={(e) => updateArrayItem('images', index, 'url', e.target.value)}
                      />
                      <Input
                        placeholder="Alt text"
                        value={img.alt}
                        onChange={(e) => updateArrayItem('images', index, 'alt', e.target.value)}
                      />
                      <Button
                        onClick={() => {
                          onOpenImagePicker(section.id, `images.${index}.url`);
                        }}
                        variant="outline"
                        className="w-full"
                        type="button"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Browse/Upload Images
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('images', { url: '', alt: 'Gallery image' })}
                  variant="outline"
                  className="w-full"
                >
                  + Add Image
                </Button>
              </div>
            </>
          )}

          {/* TESTIMONIALS SECTION */}
          {section.type === 'testimonials' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Testimonials</label>
                {section.content.testimonials?.map((testimonial, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Testimonial {index + 1}</span>
                      <Button
                        onClick={() => removeArrayItem('testimonials', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <textarea
                        placeholder="Testimonial text"
                        value={testimonial.text}
                        onChange={(e) => updateArrayItem('testimonials', index, 'text', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows="3"
                      />
                      <Input
                        placeholder="Author name"
                        value={testimonial.author}
                        onChange={(e) => updateArrayItem('testimonials', index, 'author', e.target.value)}
                      />
                      <Input
                        placeholder="Role/Company"
                        value={testimonial.role}
                        onChange={(e) => updateArrayItem('testimonials', index, 'role', e.target.value)}
                      />
                      <Input
                        placeholder="Rating (1-5)"
                        type="number"
                        min="1"
                        max="5"
                        value={testimonial.rating}
                        onChange={(e) => updateArrayItem('testimonials', index, 'rating', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('testimonials', { text: 'Great service!', author: 'Customer Name', role: 'Company', rating: 5 })}
                  variant="outline"
                  className="w-full"
                >
                  + Add Testimonial
                </Button>
              </div>
            </>
          )}

          {/* PRICING SECTION */}
          {section.type === 'pricing' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading (optional)</label>
                <Input
                  value={section.content.subheading || ''}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Pricing Plans</label>
                {section.content.plans?.map((plan, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{plan.name}</span>
                      <Button
                        onClick={() => removeArrayItem('plans', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Plan name"
                        value={plan.name}
                        onChange={(e) => updateArrayItem('plans', index, 'name', e.target.value)}
                      />
                      <Input
                        placeholder="Price (without $)"
                        value={plan.price}
                        onChange={(e) => updateArrayItem('plans', index, 'price', e.target.value)}
                      />
                      <Input
                        placeholder="Period (e.g., per month)"
                        value={plan.period}
                        onChange={(e) => updateArrayItem('plans', index, 'period', e.target.value)}
                      />
                      <Input
                        placeholder="Button text"
                        value={plan.buttonText}
                        onChange={(e) => updateArrayItem('plans', index, 'buttonText', e.target.value)}
                      />
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={plan.highlighted || false}
                          onChange={(e) => updateArrayItem('plans', index, 'highlighted', e.target.checked)}
                        />
                        <span className="text-sm">Highlight this plan</span>
                      </label>
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('plans', { name: 'New Plan', price: '99', period: 'per month', buttonText: 'Get Started', highlighted: false })}
                  variant="outline"
                  className="w-full"
                >
                  + Add Plan
                </Button>
              </div>
            </>
          )}

          {/* TEAM SECTION */}
          {section.type === 'team' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading (optional)</label>
                <Input
                  value={section.content.subheading || ''}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Team Members</label>
                {section.content.members?.map((member, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{member.name}</span>
                      <Button
                        onClick={() => removeArrayItem('members', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Name"
                        value={member.name}
                        onChange={(e) => updateArrayItem('members', index, 'name', e.target.value)}
                      />
                      <Input
                        placeholder="Role"
                        value={member.role}
                        onChange={(e) => updateArrayItem('members', index, 'role', e.target.value)}
                      />
                      <Input
                        placeholder="Bio (optional)"
                        value={member.bio || ''}
                        onChange={(e) => updateArrayItem('members', index, 'bio', e.target.value)}
                      />
                      <Input
                        placeholder="Image URL"
                        value={member.image || ''}
                        onChange={(e) => updateArrayItem('members', index, 'image', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('members', { name: 'Team Member', role: 'Position', bio: '', image: '' })}
                  variant="outline"
                  className="w-full"
                >
                  + Add Team Member
                </Button>
              </div>
            </>
          )}

          {/* FAQ SECTION */}
          {section.type === 'faq' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading (optional)</label>
                <Input
                  value={section.content.subheading || ''}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Questions & Answers</label>
                {section.content.faqs?.map((faq, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">FAQ {index + 1}</span>
                      <Button
                        onClick={() => removeArrayItem('faqs', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Question"
                        value={faq.question}
                        onChange={(e) => updateArrayItem('faqs', index, 'question', e.target.value)}
                      />
                      <textarea
                        placeholder="Answer"
                        value={faq.answer}
                        onChange={(e) => updateArrayItem('faqs', index, 'answer', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows="2"
                      />
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('faqs', { question: 'Your question?', answer: 'Your answer here.' })}
                  variant="outline"
                  className="w-full"
                >
                  + Add FAQ
                </Button>
              </div>
            </>
          )}

          {/* NEWSLETTER SECTION */}
          {section.type === 'newsletter' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading</label>
                <Input
                  value={section.content.subheading}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Button Text</label>
                <Input
                  value={section.content.buttonText}
                  onChange={(e) => updateField('buttonText', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Placeholder Text</label>
                <Input
                  value={section.content.placeholder}
                  onChange={(e) => updateField('placeholder', e.target.value)}
                />
              </div>
              <ColorPicker
                label="Background Color"
                value={section.content.backgroundColor || '#1e40af'}
                onChange={(color) => updateField('backgroundColor', color)}
              />
              <ColorPicker
                label="Text Color"
                value={section.content.textColor || '#ffffff'}
                onChange={(color) => updateField('textColor', color)}
              />
            </>
          )}

          {/* VIDEO SECTION */}
          {section.type === 'video' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading (optional)</label>
                <Input
                  value={section.content.subheading || ''}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Video Embed URL</label>
                <Input
                  value={section.content.videoUrl}
                  onChange={(e) => updateField('videoUrl', e.target.value)}
                  placeholder="https://www.youtube.com/embed/..."
                />
                <p className="text-xs text-gray-500 mt-1">Use YouTube embed URL or Vimeo embed URL</p>
              </div>
            </>
          )}

          {/* STATS SECTION */}
          {section.type === 'stats' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subheading (optional)</label>
                <Input
                  value={section.content.subheading || ''}
                  onChange={(e) => updateField('subheading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">Statistics</label>
                {section.content.stats?.map((stat, index) => (
                  <div key={index} className="border p-4 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Stat {index + 1}</span>
                      <Button
                        onClick={() => removeArrayItem('stats', index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Number (e.g., 10K+)"
                        value={stat.number}
                        onChange={(e) => updateArrayItem('stats', index, 'number', e.target.value)}
                      />
                      <Input
                        placeholder="Label"
                        value={stat.label}
                        onChange={(e) => updateArrayItem('stats', index, 'label', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => addArrayItem('stats', { number: '100+', label: 'Stat Label' })}
                  variant="outline"
                  className="w-full"
                >
                  + Add Stat
                </Button>
              </div>
              <ColorPicker
                label="Background Color"
                value={section.content.backgroundColor || '#1f2937'}
                onChange={(color) => updateField('backgroundColor', color)}
              />
              <ColorPicker
                label="Text Color"
                value={section.content.textColor || '#ffffff'}
                onChange={(color) => updateField('textColor', color)}
              />
            </>
          )}

          {/* CONTACT SECTION */}
          {section.type === 'contact' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Heading</label>
                <Input
                  value={section.content.heading}
                  onChange={(e) => updateField('heading', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input
                  value={section.content.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  type="email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <Input
                  value={section.content.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <Input
                  value={section.content.address}
                  onChange={(e) => updateField('address', e.target.value)}
                />
              </div>
              <ColorPicker
                label="Background Color"
                value={section.content.backgroundColor || '#1f2937'}
                onChange={(color) => updateField('backgroundColor', color)}
              />
              <ColorPicker
                label="Text Color"
                value={section.content.textColor || '#ffffff'}
                onChange={(color) => updateField('textColor', color)}
              />
            </>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={onClose} className="flex-1 bg-blue-600 hover:bg-blue-700">
              Done Editing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
